import { localDate } from '@/platform/lib/utils';
import {
    Button,
    Divider,
    Form,
    FormGroup,
    Input,
    Message,
    Select,
} from '@/platform/ui';
import { router, useForm, usePage } from '@inertiajs/react';
import { useEffect } from 'react';
import SidePanel from './SidePanel';

// Same rule as the legacy upload: a single entitlement waiting for or holding its DGV is picked automatically.
const AUTO_SELECT_STATES = [102, 200];

const period = (start, end) =>
    `${localDate(start)} - ${end ? localDate(end) : 'heden'}`;

const LkvFields = ({ data, setData, errors }) => {
    const { lkv_upload: lkvUpload } = usePage().props;

    if (!lkvUpload) {
        return <p className='text-sm text-gray-500'>LKV-gegevens laden...</p>;
    }

    const { employments, entitlements, categories } = lkvUpload;

    if (entitlements.length === 0 && employments.length === 0) {
        return (
            <Message variant='error'>
                Deze werknemer heeft geen dienstverbanden en geen LKV-rechten.
            </Message>
        );
    }

    const autoSelected =
        entitlements.length === 1 &&
        AUTO_SELECT_STATES.includes(entitlements[0].state);

    if (autoSelected) {
        return (
            <Message variant='success'>
                Het document wordt gekoppeld aan het LKV-recht{' '}
                {entitlements[0].category_label} met status{' '}
                {entitlements[0].state_label}.
            </Message>
        );
    }

    return (
        <>
            <Message variant='warning'>
                Kies een bestaand LKV-recht, of een dienstverband en categorie
                om een nieuw LKV-recht aan te maken.
            </Message>

            <Input
                type='date'
                label='Datum uit het bestand'
                value={data.date}
                onChange={event => setData('date', event.target.value)}
                error={errors.date}
            />

            {entitlements.length > 0 && (
                <Select
                    label='LKV-recht'
                    value={data.lkv_entitlement_id}
                    onChange={event =>
                        setData(previous => ({
                            ...previous,
                            lkv_entitlement_id: event.target.value,
                            base_employment_id: '',
                            lkv_category: '',
                        }))
                    }
                    options={[
                        { id: '', name: 'Geen, maak een nieuw recht aan' },
                        ...entitlements.map(entitlement => ({
                            id: entitlement.id,
                            name: `${entitlement.category_label} / ${entitlement.state_label} (${period(entitlement.start_date, entitlement.end_date)})`,
                        })),
                    ]}
                    error={errors.lkv_entitlement_id}
                />
            )}

            {!data.lkv_entitlement_id && (
                <>
                    <Select
                        label='Dienstverband'
                        placeholder='Kies een dienstverband'
                        value={data.base_employment_id}
                        onChange={event =>
                            setData('base_employment_id', event.target.value)
                        }
                        options={employments.map(employment => ({
                            id: employment.id,
                            name: period(
                                employment.starts_at,
                                employment.ends_at
                            ),
                        }))}
                        error={errors.base_employment_id}
                    />
                    <Select
                        label='Categorie'
                        placeholder='Kies een categorie'
                        value={data.lkv_category}
                        onChange={event =>
                            setData('lkv_category', event.target.value)
                        }
                        options={categories}
                        error={errors.lkv_category}
                    />
                </>
            )}
        </>
    );
};

const DocumentUploadPanel = ({ onClose }) => {
    const {
        employee,
        labels,
        lkv_labels: lkvLabels,
        lkv_upload: lkvUpload,
    } = usePage().props;

    const { data, setData, post, processing, progress, errors } = useForm({
        label: '',
        file: null,
        date: '',
        lkv_entitlement_id: '',
        base_employment_id: '',
        lkv_category: '',
    });

    const isLkvDocument = lkvLabels.includes(data.label);

    useEffect(() => {
        if (isLkvDocument && !lkvUpload) {
            router.reload({ only: ['lkv_upload'] });
        }
    }, [isLkvDocument, lkvUpload]);

    // Preselect like the legacy upload, so the common case needs no extra choices.
    useEffect(() => {
        if (!isLkvDocument || !lkvUpload) {
            return;
        }

        const { employments, entitlements } = lkvUpload;

        if (
            entitlements.length === 1 &&
            AUTO_SELECT_STATES.includes(entitlements[0].state)
        ) {
            setData('lkv_entitlement_id', entitlements[0].id);
        } else if (employments.length === 1 && entitlements.length === 0) {
            setData('base_employment_id', employments[0].id);
        }
    }, [data.label, isLkvDocument, lkvUpload]);

    const handleSubmit = event => {
        event.preventDefault();

        post(
            `/customers/${employee.customer_id}/employees/${employee.id}/documents`,
            {
                reset: ['employee', 'navigation'],
                preserveScroll: true,
                onSuccess: onClose,
            }
        );
    };

    return (
        <SidePanel
            title='Document uploaden'
            subtitle='Het document wordt gekoppeld aan deze werknemer.'
            onClose={onClose}
            footer={
                <div className='flex justify-end gap-2'>
                    <Button type='button' onClick={onClose}>
                        Annuleren
                    </Button>
                    <Button
                        type='submit'
                        form='document-upload'
                        variant='primary'
                        loading={processing}
                        disabled={!data.file}
                    >
                        Uploaden
                    </Button>
                </div>
            }
        >
            <Form id='document-upload' onSubmit={handleSubmit}>
                <FormGroup>
                    <Select
                        label='Soort bestand'
                        value={data.label}
                        onChange={event =>
                            setData(previous => ({
                                ...previous,
                                label: event.target.value,
                                date: '',
                                lkv_entitlement_id: '',
                                base_employment_id: '',
                                lkv_category: '',
                            }))
                        }
                        options={[{ id: '', name: 'Ongesorteerd' }, ...labels]}
                        error={errors.label}
                    />
                    <Input
                        type='file'
                        label='Bestand'
                        description={
                            isLkvDocument ? 'Alleen PDF-bestanden.' : undefined
                        }
                        accept={isLkvDocument ? '.pdf' : undefined}
                        required
                        onChange={event =>
                            setData('file', event.target.files[0] ?? null)
                        }
                        error={errors.file}
                    />

                    {isLkvDocument && (
                        <>
                            <Divider content='LKV' />
                            <LkvFields
                                data={data}
                                setData={setData}
                                errors={errors}
                            />
                        </>
                    )}
                </FormGroup>
            </Form>

            {progress && (
                <div
                    role='progressbar'
                    aria-label='Uploadvoortgang'
                    aria-valuenow={progress.percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    className='h-2 overflow-hidden rounded-full bg-gray-200'
                >
                    <div
                        className='bg-primary-600 h-full transition-all'
                        style={{ width: `${progress.percentage}%` }}
                    />
                </div>
            )}
        </SidePanel>
    );
};

export default DocumentUploadPanel;
